import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Table,
  Input,
  Button,
  Typography,
  Space,
  message,
  Select,
  Tooltip,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  DownloadOutlined,
  ArrowRightOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { useTender } from "../../../contexts/TenderContext";
import MainLayout from "../../layout/MainLayout";
import { useExcelImportExport } from "../../../hooks/useExcelImportExport";
import { ExcelImportExport } from "../../common/ExcelImportExport";
import { ExcelColumn } from "../../../utils/excelUtils";
import { COLORS, EVALUATE_CODES, EvaluateCode } from "../../../utils/constants";
import { useNavigate } from "react-router-dom";
import { prepareTenderForAPI } from "../../../utils/storage";
import { useCreateBillOfQuantities } from "../../../hooks/useBillOfQuantities";
import NumberInput from "../../common/NumberInput";
import { useAuth } from "../../../hooks/useAuth";
import { useScrollToTop } from "../../../hooks/useScrollToTop";
// import { useChangeDetection } from "../../../hooks/useChangeDetection";
import { useTableRenderDeferral } from "../../../hooks/useTableRenderDeferral";
import TableLoadingPlaceholder from "../../common/TableLoadingPlaceholder";
import DeleteConfirmationModal from "../../common/DeleteConfirmationModal";
import { ColumnConfigModal, selectStyles } from "../../common/ColumnConfigModal";
import BufferedTextInput from "../../common/BufferedTextInput";
import { isDuplicatePlotNo } from "../../../utils/plotNoValidation";
import { useGroundworksTotalCalculations } from "../../../hooks/useGroundworksTotalCalculations";
import { forceSaveBothBoqEndpoints } from "../../../utils/boqVersionUtils";
import { useItemLibrary } from "../../../hooks/useItemLibrary";
import { addColumnCodes } from "@/utils/addColumnCodes";
import { PLOT_PAVINGS_REFERENCES } from "@/utils/plotPavingsReferenceMapping";
import { normaliseDecimalMmInLabel } from "../../../utils/dimensionLabel";

const { Title, Text } = Typography;
const { Option } = Select;

const excelColumns: ExcelColumn[] = [
  { key: "plotNo", title: "Plot No" },
  { key: "housetype", title: "House type" },
  // Paving Paths
  { key: "pavingLevelCompact", title: "Level & Compact (m²)" },
  { key: "pavingGeotextileLayer", title: "Geotextile Layer (m²)" },
  { key: "pavingSubBase", title: "xxxmm thick Stone bed (m²)" },
  { key: "pavingPlainGreyPavings", title: "Slabs (m²)" },
  { key: "slab900wSteps", title: "900w Steps (Nr)" },
  { key: "slab1200wSteps", title: "1200w Steps (Nr)" },
  { key: "slabGravelMarginPea", title: "Gravel Margin in pea gravel (Nr)" },
  {
    key: "slabGravelMarginCotswold",
    title: "Gravel Margin in Cotswold gravel (Nr)",
  },
  { key: "slabThresholdDrain", title: "Threshold Drain (Nr)" },
  { key: "slabShedBase", title: "Shed Base (Nr)" },
  { key: "slabCycleHoops", title: "Cycle Hoops (Nr)" },
  { key: "slabRotaryDryer", title: "Rotary Dryer (Nr)" },
  // Macadam Paths
  { key: "macadamLevelCompact", title: "Level & Compact (m²)" },
  { key: "macadamGeotextileLayer", title: "Geotextile Layer (m²)" },
  { key: "macadamSubBase", title: "xxxmm thick MOT Type 1 (m²)" },
  { key: "macadamDbBase", title: "xxmm DB Base (m²)" },
  { key: "macadamDbBinder", title: "xxmm DB Binder (m²)" },
  { key: "macadamSealingGrit", title: "Sealing Grit (m²)" },
  { key: "macadamSweepTack", title: "Sweep & Tack (m²)" },
  { key: "macadamDbSurfaceCourse", title: "xxmm DB Surface course (m²)" },
  { key: "macadamHraSurfaceCourse", title: "xxmm HRA Surface course (m²)" },
  { key: "macadamSmaSurfaceCourse", title: "xxmm SMA Surface course (m²)" },
  { key: "macadamEdgings", title: "Edgings (m)" },
];

interface PlotPavingsRow {
  key: string;
  plotNo: string;
  housetype: string;
  // Paving Paths
  pavingLevelCompact: number;
  pavingGeotextileLayer: number;
  pavingSubBase: number;
  pavingPlainGreyPavings: number;
  slab900wSteps: number;
  slab1200wSteps: number;
  slabGravelMarginPea: number;
  slabGravelMarginCotswold: number;
  slabThresholdDrain: number;
  slabShedBase: number;
  slabCycleHoops: number;
  slabRotaryDryer: number;
  // Macadam Paths
  macadamLevelCompact: number;
  macadamGeotextileLayer: number;
  macadamSubBase: number;
  macadamDbBase: number;
  macadamDbBinder: number;
  macadamSealingGrit: number;
  macadamSweepTack: number;
  macadamDbSurfaceCourse: number;
  macadamHraSurfaceCourse: number;
  macadamSmaSurfaceCourse: number;
  macadamEdgings: number;
}

const PlotPavings: React.FC = () => {
  // Scroll to top on page load
  useScrollToTop();

  const { state: tender, updateField, saveTender } = useTender();
  const { user } = useAuth();
  const navigate = useNavigate();
  const createBoqMutation = useCreateBillOfQuantities();

  // Format column title to display field name and unit separately
  const formatColumnTitle = React.useCallback(
    (baseTitle: string): React.ReactNode => {
      const unitMatch = baseTitle.match(/\s+\((m[²³]|m|Nr|t)\)$/);
      let titleWithoutUnit = baseTitle;
      let unit = "";

      if (unitMatch) {
        unit = unitMatch[1];
        titleWithoutUnit = baseTitle.replace(/\s+\((m[²³]|m|Nr|t)\)$/, "");
      }

      if (unit) {
        return (
          <div className="text-center">
            <Tooltip title={titleWithoutUnit}>
              <div className="gw-header-title">{titleWithoutUnit}</div>
            </Tooltip>
            <div className="gw-header-unit text-xs text-gray-500">{unit}</div>
          </div>
        );
      }
      return (
        <Tooltip title={baseTitle}>
          <div className="gw-header-title">{baseTitle}</div>
        </Tooltip>
      );
    },
    []
  );

  // Checkbox state logic for groundworks totals
  const getCheckboxState = (key: string, fallbackPercent: number): boolean => {
    const saved = localStorage.getItem(key);
    if (saved !== null) {
      return JSON.parse(saved);
    }
    return fallbackPercent > 0;
  };

  const preliminariesPercent = tender.globalSettings?.groundworks?.preliminaries || 0;
  const ohpPercent = tender.globalSettings?.groundworks?.overheadAndProfit || 0;
  const discountPercent = tender.globalSettings?.groundworks?.discount || 0;

  const includePreliminaries = getCheckboxState("groundworks_includePreliminariesDisplay", preliminariesPercent);
  const includeOhp = getCheckboxState("groundworks_includeOhpDisplay", ohpPercent);
  const includeDiscount = getCheckboxState("groundworks_includeDiscountDisplay", discountPercent);

  const groundworksTotals = useGroundworksTotalCalculations({
    includePreliminaries,
    includeOhp,
    includeDiscount,
  });
  const { data: itemLibraryData } = useItemLibrary(tender.region || "");

  const [data, setData] = useState<PlotPavingsRow[]>([]);
  const [columnHeaders, setColumnHeaders] = useState<Record<string, string>>(
    {}
  );
  const [dimensionValues, setDimensionValues] = useState<
    Record<string, string>
  >({});

  // Merge static references with any evaluate codes stored in dimensionValues (e.g. ALP041 for DB Base)
  const effectivePavingsRefs = React.useMemo(() => {
    const merged = { ...PLOT_PAVINGS_REFERENCES };
    Object.entries(dimensionValues).forEach(([key, val]) => {
      if (/^(ALP|GWALP|ACL|ZEV|HAUL)[A-Z0-9]/i.test(val)) {
        merged[key] = { code: val.toUpperCase() };
      }
    });
    return merged;
  }, [dimensionValues]);

  // Temporary state for pending header changes (only applied when user clicks Apply)
  const [tempDimensionValues, setTempDimensionValues] = useState<
    Record<string, string>
  >({});
  const [tempColumnHeaders, setTempColumnHeaders] = useState<
    Record<string, string>
  >({});
  const [activeTab, setActiveTab] = useState<"paving-paths" | "macadam-paths">(
    "paving-paths"
  );
  // Defers the Paving Paths / Macadam Paths tables' mount by one paint cycle
  // so a tender with a lot of plots doesn't make this step look frozen while
  // the table's render/layout blocks the main thread - see
  // useTableRenderDeferral's own doc comment. Re-keyed on `activeTab` since
  // switching tabs here unmounts/mounts a brand new Table, not just a
  // dataSource update - so the loader should reappear on every tab switch,
  // not just the initial mount. Purely a perceived-lag fix: once it flips
  // false the tables render exactly as before.
  const isTableLoading = useTableRenderDeferral([activeTab]);
  const [tableKey, setTableKey] = useState(0);
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState<string | null>(null);
  const [keysToDelete, setKeysToDelete] = useState<string[]>([]);
  const [selectedPavingKeys, setSelectedPavingKeys] = useState<React.Key[]>([]);
  const [configModalVisible, setConfigModalVisible] = useState(false);

  // Ref to track latest tender.groundworks to avoid stale closure issues in save effects
  const groundworksRef = useRef(tender?.groundworks);
  useEffect(() => {
    groundworksRef.current = tender?.groundworks;
  }, [tender?.groundworks]);

  // Track previous configModalVisible state to detect when modal opens
  const prevConfigModalVisibleRef = useRef(configModalVisible);

  // Reset temp values when modal opens (configModalVisible goes from false to true)
  useEffect(() => {
    if (configModalVisible && !prevConfigModalVisibleRef.current) {
      // Modal just opened, reset temp values to current values
      setTempDimensionValues({ ...dimensionValues });
      setTempColumnHeaders({ ...columnHeaders });
    }
    prevConfigModalVisibleRef.current = configModalVisible;
  }, [configModalVisible, dimensionValues, columnHeaders]);

  // Change detection hook
  /* const {
    hasChanges,
    trackFieldChange,
    trackRowAdded,
    trackRowDeleted,
    resetChanges,
    getChangeSummary,
  } = useChangeDetection<PlotPavingsRow[]>({
    pageId: "plot-pavings",
    onChangeDetected: (changes) => {
    },
  }); */

  // Force table re-render when switching tabs
  React.useEffect(() => {
    setTableKey((prev) => prev + 1);
  }, [activeTab]);

  // Force table re-render when column headers change
  React.useEffect(() => {
    setTableKey((prev) => prev + 1);
  }, [columnHeaders]);

  const {
    importLoading,
    importModalVisible,
    setImportModalVisible,
    handleExportExcel,
    handleDownloadTemplate,
    handleImportExcel: baseHandleImportExcel,
  } = useExcelImportExport<PlotPavingsRow>({
    filename: "PlotPavings",
    sheetName: "Plot Pavings",
    columns: excelColumns,
    data: data,
    onImport: (importedData) => {
      const processedData = importedData.map((row, index) => {
        const emptyRow = createEmptyRow(`imported_${Date.now()}_${index}`);
        const merged = { ...emptyRow, ...row };

        // Round all numeric fields to 2 decimals
        Object.keys(merged).forEach((key) => {
          if (typeof merged[key] === "number" && key !== "key") {
            merged[key] = Math.round(merged[key] * 1000) / 1000;
          }
        });

        return merged;
      });
      setData(processedData);
      updateField("groundworks", {
        ...tender.groundworks,
        plotPavingsTable: processedData,
      });
    },
    skipValidation: true,
  });

  const handleImportExcel = async (file: File) => {
    setImportModalVisible(false);

    try {
      const XLSX = await import("xlsx");
      const reader = new FileReader();

      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: "array" });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];

          const jsonData = XLSX.utils.sheet_to_json(worksheet, {
            header: 1,
            raw: true,
            blankrows: false,
          }) as any[][];


          let headerRowIndex = -1;
          for (let i = 0; i < jsonData.length; i++) {
            const row = jsonData[i];
            if (
              row.some(
                (cell) =>
                  String(cell).includes("Plot") && String(cell).includes("No")
              )
            ) {
              headerRowIndex = i;
              break;
            }
          }


          if (headerRowIndex === -1) {
            alert("Could not find header row with 'Plot No'");
            return;
          }

          const headers = jsonData[headerRowIndex];

          // Skip columns C, D, E, F, G, H, I (indices 2-8)
          // Data starts at column J (index 9)
          const skipColumnsCount = 7; // Columns C through I
          const dataStartIndex = 2 + skipColumnsCount; // Start at index 9 (column J)


          // Extract column headers and dimension values
          const extractedHeaders: Record<string, string> = {};
          const extractedDimensionValues: Record<string, string> = {};

          // Build multi-row headers to search (up to 4 rows below headerRowIndex)
          const headerRowsToSearch: string[][] = [];
          for (let r = headerRowIndex; r <= Math.min(headerRowIndex + 4, jsonData.length - 1); r++) {
            if (jsonData[r]) {
              headerRowsToSearch.push(jsonData[r].map((cell: any) => String(cell || "").trim()));
            }
          }

          // Define fixed text for numeric dimension columns
          const dimensionFixedText: Record<string, string> = {
            pavingSubBase: "thick Stone bed",
            macadamSubBase: "thick MOT Type 1",
          };

          // Dropdown columns that match against evaluate codes
          const dropdownColumns: Record<string, string[]> = {
            pavingPlainGreyPavings: ["GWALP007", "GWALP008", "GWALP040", "GWALP041", "GWALP042", "GWALP043", "GWALP044", "GWALP045"],
            macadamDbBase: ["ALP041", "ALP042"],
            macadamDbBinder: ["ALP043", "ALP044", "ALP045", "ALP050", "ALP053"],
            macadamDbSurfaceCourse: ["ALP048", "ALP049", "ALP051"],
            macadamHraSurfaceCourse: ["ALP049", "ALP051"],
            macadamSmaSurfaceCourse: ["ALP052"],
          };

          // Map headers starting from column index 9 (after Plot No, House type, and skipped columns)
          excelColumns.forEach((col, index) => {
            if (index >= 2) {
              // Skip "plotNo" and "housetype"
              const headerIndex = dataStartIndex + (index - 2); // Offset by skipped columns
              if (headerIndex < headers.length && headers[headerIndex]) {
                const headerValue = String(headers[headerIndex]).trim();
                extractedHeaders[col.key] = headerValue;

                // Check for dropdown columns first (Slabs, DB Base, DB Binder, Surface courses)
                if (dropdownColumns[col.key]) {
                  // Search across multiple header rows for a matching evaluate code label
                  for (const headerRow of headerRowsToSearch) {
                    const cellValue = headerRow[headerIndex];
                    if (!cellValue) continue;
                    for (const code of dropdownColumns[col.key]) {
                      if (
                        EVALUATE_CODES[code as EvaluateCode] &&
                        EVALUATE_CODES[code as EvaluateCode].label.toLowerCase() === cellValue.toLowerCase()
                      ) {
                        extractedDimensionValues[col.key] = code;
                        break;
                      }
                    }
                    if (extractedDimensionValues[col.key]) break;
                  }
                }
                // Check if this column has dimension patterns in default title
                else if (/\b(x{2,}(?:mm)?)\b/gi.test(col.title)) {
                  const fixedText = dimensionFixedText[col.key];
                  let found = false;

                  // Search across multiple header rows
                  for (const headerRow of headerRowsToSearch) {
                    if (found) break;
                    const cellValue = headerRow[headerIndex];
                    if (!cellValue) continue;

                    if (fixedText) {
                      // Find the fixed text portion and extract the leading dimension before it
                      const fixedTextIndex = cellValue.toLowerCase().indexOf(fixedText.toLowerCase());
                      if (fixedTextIndex > 0) {
                        const leadingText = cellValue.substring(0, fixedTextIndex).trim();
                        const dimMatch = leadingText.match(/(\d+(?:\.\d+)?)\s*mm/i) || leadingText.match(/(\d+(?:\.\d+)?)\s*$/);
                        if (dimMatch) {
                          extractedDimensionValues[col.key] = dimMatch[1];
                          found = true;
                        }
                      }
                    }

                    if (!found) {
                      // Fallback: extract first number followed by mm
                      const dimensionMatch = cellValue.match(/\b(\d+(?:\.\d+)?)\s*mm\b/i);
                      if (dimensionMatch) {
                        extractedDimensionValues[col.key] = dimensionMatch[1];
                        found = true;
                      }
                    }
                  }
                }
              }
            }
          });


          setColumnHeaders(extractedHeaders);
          setDimensionValues(extractedDimensionValues);

          const allDataRows = jsonData.slice(headerRowIndex + 1);

          // Skip only the first row (totals row)
          const dataRows = allDataRows.slice(1);


          // Find column indices by matching header names
          const plotNoIdx = headers.findIndex(
            (h: any) => String(h).includes("Plot") && String(h).includes("No")
          );
          const housetypeIdx = headers.findIndex(
            (h: any) =>
              String(h).toLowerCase().includes("house") &&
              String(h).toLowerCase().includes("type")
          );


          const allImportedRows: PlotPavingsRow[] = dataRows
            .filter(
              (row) =>
                row &&
                row.length > 0 &&
                row.some(
                  (cell) => cell !== null && cell !== undefined && cell !== ""
                )
            )
            .map((row, index) => {
              return {
                key: `imported_${Date.now()}_${index}`,
                plotNo: String(row[plotNoIdx] || ""),
                housetype: String(row[housetypeIdx] || ""),
                // Data starts 9 columns after Plot No (skipping columns C-I which are indices 2-8)
                pavingLevelCompact:
                  Math.round((Number(row[plotNoIdx + 9]) || 0) * 1000) / 1000,
                pavingGeotextileLayer:
                  Math.round((Number(row[plotNoIdx + 10]) || 0) * 1000) / 1000,
                pavingSubBase:
                  Math.round((Number(row[plotNoIdx + 11]) || 0) * 1000) / 1000,
                pavingPlainGreyPavings:
                  Math.round((Number(row[plotNoIdx + 12]) || 0) * 1000) / 1000,
                slab900wSteps:
                  Math.round((Number(row[plotNoIdx + 13]) || 0) * 1000) / 1000,
                slab1200wSteps:
                  Math.round((Number(row[plotNoIdx + 14]) || 0) * 1000) / 1000,
                slabGravelMarginPea:
                  Math.round((Number(row[plotNoIdx + 15]) || 0) * 1000) / 1000,
                slabGravelMarginCotswold:
                  Math.round((Number(row[plotNoIdx + 16]) || 0) * 1000) / 1000,
                slabThresholdDrain:
                  Math.round((Number(row[plotNoIdx + 17]) || 0) * 1000) / 1000,
                slabShedBase:
                  Math.round((Number(row[plotNoIdx + 18]) || 0) * 1000) / 1000,
                slabCycleHoops:
                  Math.round((Number(row[plotNoIdx + 19]) || 0) * 1000) / 1000,
                slabRotaryDryer:
                  Math.round((Number(row[plotNoIdx + 20]) || 0) * 1000) / 1000,
                macadamLevelCompact:
                  Math.round((Number(row[plotNoIdx + 21]) || 0) * 1000) / 1000,
                macadamGeotextileLayer:
                  Math.round((Number(row[plotNoIdx + 22]) || 0) * 1000) / 1000,
                macadamSubBase:
                  Math.round((Number(row[plotNoIdx + 23]) || 0) * 1000) / 1000,
                macadamDbBase:
                  Math.round((Number(row[plotNoIdx + 24]) || 0) * 1000) / 1000,
                macadamDbBinder:
                  Math.round((Number(row[plotNoIdx + 25]) || 0) * 1000) / 1000,
                macadamSealingGrit:
                  Math.round((Number(row[plotNoIdx + 26]) || 0) * 1000) / 1000,
                macadamSweepTack:
                  Math.round((Number(row[plotNoIdx + 27]) || 0) * 1000) / 1000,
                macadamDbSurfaceCourse:
                  Math.round((Number(row[plotNoIdx + 28]) || 0) * 1000) / 1000,
                macadamHraSurfaceCourse:
                  Math.round((Number(row[plotNoIdx + 29]) || 0) * 1000) / 1000,
                macadamSmaSurfaceCourse:
                  Math.round((Number(row[plotNoIdx + 30]) || 0) * 1000) / 1000,
                macadamEdgings:
                  Math.round((Number(row[plotNoIdx + 31]) || 0) * 1000) / 1000,
              };
            })
            .filter((row) => {
              const plotNoStr = String(row.plotNo || "").trim();
              const hasPlotNo =
                plotNoStr !== "" &&
                plotNoStr !== "0" &&
                plotNoStr !== "null" &&
                plotNoStr !== "undefined";
              const housetypeStr = String(row.housetype || "").trim();
              const hasValidHouseType =
                housetypeStr !== "" &&
                !housetypeStr.toLowerCase().includes("total") &&
                !housetypeStr.toLowerCase().includes("sum") &&
                isNaN(Number(housetypeStr)) && // Not purely numeric
                housetypeStr.match(/[a-zA-Z]/); // Contains letters
              return hasPlotNo && hasValidHouseType;
            });


          if (allImportedRows.length === 0) {
            alert("No valid data found to import");
            return;
          }

          setData(allImportedRows);
          updateField("groundworks", {
            ...tender.groundworks,
            plotPavingsTable: allImportedRows,
          });
          alert(`Successfully imported ${allImportedRows.length} rows`);
        } catch (error) {
          console.error("Import error:", error);
          alert(`Import failed: ${error.message}`);
        }
      };

      reader.readAsArrayBuffer(file);
    } catch (error) {
      console.error("Import error:", error);
      alert(`Import failed: ${error.message}`);
    }
  };

  useEffect(() => {
    if (tender?.groundworks?.plotPavingsTable) {
      setData(tender.groundworks.plotPavingsTable);
    } else {
      // Initialize with empty row
      setData([createEmptyRow()]);
    }
  }, [tender?.groundworks?.plotPavingsTable]);

  // Load dimension values and column headers from tender context
  useEffect(() => {
    // Wait for tender data to be available
    if (!tender?.groundworks) return;

    // Only run once
    if (isInitialLoadComplete) return;

    let loadedHeaders: Record<string, string> = {};
    let loadedDimensions: Record<string, string> = {};

    // Load dimension values first
    if (tender?.groundworks?.plotPavingsDimensionValues) {
      loadedDimensions = tender.groundworks.plotPavingsDimensionValues;
      setDimensionValues(loadedDimensions);
    }

    // Load column headers
    if (tender?.groundworks?.plotPavingsColumnHeaders) {
      const headers = tender.groundworks.plotPavingsColumnHeaders;
      loadedHeaders = headers;
    }

    // Sub base fields that store numeric values (e.g., "150" -> "150mm Sub base")
    const subBaseFields = ["pavingSubBase", "macadamSubBase"];

    // Reconstruct missing headers from dimension values
    Object.entries(loadedDimensions).forEach(([key, value]) => {
      if (value && typeof value === "string") {
        // Handle sub base numeric values
        if (subBaseFields.includes(key)) {
          if (!loadedHeaders[key] && value.trim() !== "") {
            const formattedValue = value.includes("mm") ? value : `${value}mm`;
            loadedHeaders[key] = key === "pavingSubBase"
              ? `${formattedValue} thick Stone bed`
              : `${formattedValue} thick MOT Type 1`;
          }
        }
        // Handle GWALP and ALP codes
        else if (value.startsWith("GWALP") || value.startsWith("ALP")) {
          const evaluateCode = value as EvaluateCode;
          if (EVALUATE_CODES[evaluateCode]) {
            // Only set if not already in loaded headers
            if (!loadedHeaders[key]) {
              loadedHeaders[key] = EVALUATE_CODES[evaluateCode].label;
            }
          }
        }
      }
    });

    setColumnHeaders(loadedHeaders);

    // Mark initial load as complete
    setIsInitialLoadComplete(true);
  }, [tender?.groundworks, isInitialLoadComplete]);

  // Save column headers and dimension values to tender context whenever they change (after initial load)
  useEffect(() => {
    if (
      isInitialLoadComplete &&
      (Object.keys(columnHeaders).length > 0 ||
        Object.keys(dimensionValues).length > 0)
    ) {
      // Use ref to get the latest groundworks state to avoid stale closure issues
      const currentGroundworks = groundworksRef.current || {};
      updateField("groundworks", {
        ...currentGroundworks,
        plotPavingsColumnHeaders: columnHeaders,
        plotPavingsDimensionValues: dimensionValues,
      });
      // Trigger immediate save
      setTimeout(() => {
        saveTender();
      }, 100);
    }
  }, [columnHeaders, dimensionValues, isInitialLoadComplete]);

  // Auto-sync columnHeaders from dimensionValues when they contain evaluate codes (ALP or GWALP)
  // This ensures headers are always reconstructed from dimension values for DB Base, DB Binder, and Surface course fields
  useEffect(() => {
    // Skip if not yet loaded (but allow running once after initial load completes)
    if (!isInitialLoadComplete && Object.keys(dimensionValues).length === 0)
      return;

    let needsUpdate = false;
    const updatedHeaders = { ...columnHeaders };

    // Sub base fields that store numeric values (e.g., "150" -> "150mm Sub base")
    const subBaseFields = ["pavingSubBase", "macadamSubBase"];

    // Fields that use dropdown selections with evaluate codes
    const evaluateCodeFields = [
      "pavingPlainGreyPavings",
      "macadamDbBase",
      "macadamDbBinder",
      "macadamDbSurfaceCourse",
      "macadamHraSurfaceCourse",
      "macadamSmaSurfaceCourse",
    ];

    Object.entries(dimensionValues).forEach(([key, value]) => {
      if (value && typeof value === "string") {
        // Handle sub base numeric values
        if (subBaseFields.includes(key)) {
          if (value.trim() !== "") {
            const formattedValue = value.includes("mm") ? value : `${value}mm`;
            const expectedLabel = key === "pavingSubBase"
              ? `${formattedValue} thick Stone bed`
              : `${formattedValue} thick MOT Type 1`;
            if (updatedHeaders[key] !== expectedLabel) {
              updatedHeaders[key] = expectedLabel;
              needsUpdate = true;
            }
          }
        }
        // Check if this is an evaluate code field
        else if (evaluateCodeFields.includes(key)) {
          if (value.startsWith("ALP") || value.startsWith("GWALP")) {
            // Handle evaluate codes - look up the label
            const evaluateCode = value as EvaluateCode;
            if (EVALUATE_CODES[evaluateCode]) {
              const expectedLabel = EVALUATE_CODES[evaluateCode].label;
              // Update if missing or different
              if (updatedHeaders[key] !== expectedLabel) {
                updatedHeaders[key] = expectedLabel;
                needsUpdate = true;
              }
            }
          }
        }
      }
    });

    if (needsUpdate) {
      setColumnHeaders(updatedHeaders);
    }
  }, [dimensionValues, isInitialLoadComplete, columnHeaders]);

  const createEmptyRow = (key?: string): PlotPavingsRow => ({
    key: key || Date.now().toString(),
    plotNo: "",
    housetype: "",
    // Paving Paths
    pavingLevelCompact: 0,
    pavingGeotextileLayer: 0,
    pavingSubBase: 0,
    pavingPlainGreyPavings: 0,
    slab900wSteps: 0,
    slab1200wSteps: 0,
    slabGravelMarginPea: 0,
    slabGravelMarginCotswold: 0,
    slabThresholdDrain: 0,
    slabShedBase: 0,
    slabCycleHoops: 0,
    slabRotaryDryer: 0,
    // Macadam Paths
    macadamLevelCompact: 0,
    macadamGeotextileLayer: 0,
    macadamSubBase: 0,
    macadamDbBase: 0,
    macadamDbBinder: 0,
    macadamSealingGrit: 0,
    macadamSweepTack: 0,
    macadamDbSurfaceCourse: 0,
    macadamHraSurfaceCourse: 0,
    macadamSmaSurfaceCourse: 0,
    macadamEdgings: 0,
  });

  const handleInputChange = (
    key: string,
    field: keyof PlotPavingsRow,
    value: string | number
  ) => {
    setData((prevData) => {
      const currentRow = prevData.find((row) => row.key === key);
      const oldValue = currentRow ? currentRow[field] : undefined;

      // Track the field change
      // trackFieldChange(field as string, oldValue, value, key);

      const newData = prevData.map((row) => {
        if (row.key === key) {
          return { ...row, [field]: value };
        }
        return row;
      });

      // Update tender context
      updateField("groundworks", {
        ...tender.groundworks,
        plotPavingsTable: newData,
      });
      return newData;
    });
  };

  const addRow = () => {
    const newRow = createEmptyRow();
    // trackRowAdded(newRow.key);
    const newData = [...data, newRow];
    setData(newData);
    updateField("groundworks", {
      ...tender.groundworks,
      plotPavingsTable: newData,
    });
  };

  const deleteRow = (key: string) => {
    setKeyToDelete(key);
    setKeysToDelete([]);
    setDeleteModalVisible(true);
  };

  const deleteSelectedRows = () => {
    setKeysToDelete(selectedPavingKeys as string[]);
    setKeyToDelete(null);
    setDeleteModalVisible(true);
  };

  const confirmDelete = () => {
    const keysBeingDeleted = keysToDelete.length > 0 ? keysToDelete : keyToDelete ? [keyToDelete] : [];
    if (keysBeingDeleted.length > 0) {
      // trackRowDeleted(keyToDelete);
      const keySet = new Set(keysBeingDeleted);
      const newData = data.filter((row) => !keySet.has(row.key));
      setData(newData);
      // Deleting the last remaining row also clears the ad-hoc extra items
      // grafted onto Plot Paving Bill Summary — see CutFills.tsx's identical
      // fix for the reasoning.
      updateField("groundworks", {
        ...tender.groundworks,
        plotPavingsTable: newData,
        ...(newData.length === 0 && { plotPavingsExtraItems: {} }),
      });
      setSelectedPavingKeys([]);
    }
    setDeleteModalVisible(false);
    setKeyToDelete(null);
    setKeysToDelete([]);
  };

  const cancelDelete = () => {
    setDeleteModalVisible(false);
    setKeyToDelete(null);
    setKeysToDelete([]);
  };

  const getScrollConfig = (columns: { width?: number | string }[]) => {
    const totalWidth = columns.reduce((sum, col) => sum + (Number(col.width) || 120), 0);
    const estimatedRowHeight = 50;
    const maxComfortableRows = 12;
    const needsVerticalScroll = data.length > maxComfortableRows;
    return { x: totalWidth, y: needsVerticalScroll ? maxComfortableRows * estimatedRowHeight : undefined };
  };

  const handleSave = async () => {
    try {
      await saveTender();
      // Note: Don't reset changes here - changes persist until "Send for Review"
    } catch (error) {
      console.error("Failed to save:", error);
    }
  };

  const handleSaveAndContinue = async () => {
    try {
      message.loading({ content: "Saving tender...", key: "saveTender", duration: 0 });

      // Update tender state with hook values
      updateField("groundworks", {
        ...tender.groundworks,
        adjustments: {
          preliminaries: groundworksTotals.preliminariesAmount,
          ohp: groundworksTotals.ohpAmount,
          discount: groundworksTotals.discountAmount,
          includePreliminaries: groundworksTotals.preliminariesPercent > 0,
          includeOhp: groundworksTotals.ohpPercent > 0,
          includeDiscount: groundworksTotals.discountPercent > 0,
        },
        totals: {
          ...tender.groundworks?.totals,
          grandTotal: groundworksTotals.grandTotal,
          totalWithAdjustments: groundworksTotals.tenderTotal,
          tenderTotal: groundworksTotals.tenderTotal,
        },
      });

      updateField("globalSettings", {
        ...tender.globalSettings,
        groundworks: {
          overheadAndProfit: groundworksTotals.ohpPercent,
          preliminaries: groundworksTotals.preliminariesPercent,
          discount: groundworksTotals.discountPercent,
        },
      });

      updateField("status", "In-progress");
      updateField("lastModified", new Date());

      await saveTender();
      await new Promise((resolve) => setTimeout(resolve, 200));

      await forceSaveBothBoqEndpoints(tender, "Save and continue from Plot Pavings", {
        grandTotal: groundworksTotals.grandTotal,
        grandTotalWithAdjustments: groundworksTotals.grandTotalWithAdjustments,
        preliminariesAmount: groundworksTotals.preliminariesAmount,
        ohpAmount: groundworksTotals.ohpAmount,
        discountAmount: groundworksTotals.discountAmount,
        tenderTotal: groundworksTotals.tenderTotal,
        preliminariesPercent: groundworksTotals.preliminariesPercent,
        ohpPercent: groundworksTotals.ohpPercent,
        discountPercent: groundworksTotals.discountPercent,
      }, user?.name, itemLibraryData);

      message.destroy("saveTender");
      setTimeout(() => navigate("/groundworks/scaffold"), 500);
    } catch (error: any) {
      console.error("❌ Error submitting tender:", error);
      message.error({
        content: `Failed to submit tender: ${
          error.response?.data?.message || error.message || "Unknown error"
        }`,
        key: "saveTender",
        duration: 5,
      });
    }
  };

  const handleSaveAndExit = async () => {
    try {
      message.loading({ content: "Saving tender...", key: "saveTender", duration: 0 });

      // Update tender state with hook values
      updateField("groundworks", {
        ...tender.groundworks,
        adjustments: {
          preliminaries: groundworksTotals.preliminariesAmount,
          ohp: groundworksTotals.ohpAmount,
          discount: groundworksTotals.discountAmount,
          includePreliminaries: groundworksTotals.preliminariesPercent > 0,
          includeOhp: groundworksTotals.ohpPercent > 0,
          includeDiscount: groundworksTotals.discountPercent > 0,
        },
        totals: {
          ...tender.groundworks?.totals,
          grandTotal: groundworksTotals.grandTotal,
          totalWithAdjustments: groundworksTotals.tenderTotal,
          tenderTotal: groundworksTotals.tenderTotal,
        },
      });

      updateField("globalSettings", {
        ...tender.globalSettings,
        groundworks: {
          overheadAndProfit: groundworksTotals.ohpPercent,
          preliminaries: groundworksTotals.preliminariesPercent,
          discount: groundworksTotals.discountPercent,
        },
      });

      updateField("status", "In-progress");
      updateField("lastModified", new Date());

      await saveTender();
      await new Promise((resolve) => setTimeout(resolve, 200));

      await forceSaveBothBoqEndpoints(tender, "Save and exit from Plot Pavings", {
        grandTotal: groundworksTotals.grandTotal,
        grandTotalWithAdjustments: groundworksTotals.grandTotalWithAdjustments,
        preliminariesAmount: groundworksTotals.preliminariesAmount,
        ohpAmount: groundworksTotals.ohpAmount,
        discountAmount: groundworksTotals.discountAmount,
        tenderTotal: groundworksTotals.tenderTotal,
        preliminariesPercent: groundworksTotals.preliminariesPercent,
        ohpPercent: groundworksTotals.ohpPercent,
        discountPercent: groundworksTotals.discountPercent,
      }, user?.name, itemLibraryData);

      message.destroy("saveTender");
      setTimeout(() => navigate("/"), 500);
    } catch (error: any) {
      console.error("❌ Error submitting tender:", error);
      message.error({
        content: `Failed to submit tender: ${
          error.response?.data?.message || error.message || "Unknown error"
        }`,
        key: "saveTender",
        duration: 5,
      });
    }
  };

  const calculateTotals = () => {
    const totals: Partial<PlotPavingsRow> = {
      pavingLevelCompact: 0,
      pavingGeotextileLayer: 0,
      pavingSubBase: 0,
      pavingPlainGreyPavings: 0,
      slab900wSteps: 0,
      slab1200wSteps: 0,
      slabGravelMarginPea: 0,
      slabGravelMarginCotswold: 0,
      slabThresholdDrain: 0,
      slabShedBase: 0,
      slabCycleHoops: 0,
      slabRotaryDryer: 0,
      macadamLevelCompact: 0,
      macadamGeotextileLayer: 0,
      macadamSubBase: 0,
      macadamDbBase: 0,
      macadamDbBinder: 0,
      macadamSealingGrit: 0,
      macadamSweepTack: 0,
      macadamDbSurfaceCourse: 0,
      macadamHraSurfaceCourse: 0,
      macadamSmaSurfaceCourse: 0,
      macadamEdgings: 0,
    };

    data.forEach((row) => {
      Object.keys(totals).forEach((key) => {
        totals[key] += Math.round((Number(row[key]) || 0) * 1000) / 1000;
      });
    });

    return totals;
  };

  // Helper function to get column title - uses dynamic header if available, otherwise falls back to default
  const getColumnTitle = React.useCallback(
    (fieldKey: string, defaultTitle: string): string => {
      // LABEL-ONLY normalisation: convert any "<decimal>mm" to integer mm in
      // the final title. Rate calculations continue to use the raw value.
      // Return stored column header (from Excel import) exactly as-is.
      if (columnHeaders[fieldKey]) {
        return columnHeaders[fieldKey];
      }

      // Fallback: substitute dimension value into the default title pattern and
      // normalise any decimal-mm result (e.g. "0.150mm" → "150mm").
      if (dimensionValues[fieldKey] && dimensionValues[fieldKey].trim() !== "") {
        let title = defaultTitle;
        title = title.replace(/\b(x{2,}(?:mm)?)\b/gi, (match) => {
          let value = dimensionValues[fieldKey];
          if (
            match.toLowerCase().endsWith("mm") &&
            !value.toLowerCase().endsWith("mm")
          ) {
            value = value + "mm";
          }
          return value;
        });
        return normaliseDecimalMmInLabel(title);
      }

      return defaultTitle;
    },
    [columnHeaders, dimensionValues]
  );

  // Extract dimension fields from excel columns and categorize by tab
  const dimensionFields = React.useMemo(() => {
    const fieldsWithDimensions: Array<{
      key: string;
      title: string;
      category: "paving" | "macadam";
    }> = [];

    // Check excelColumns for fields with dimension patterns (xxmm, xxxmm, etc.)
    excelColumns.forEach((col) => {
      const defaultTitle = col.title;
      // Check if DEFAULT header contains dimension patterns like xxmm, xxxmm, xxxx
      if (/\b(x{2,}(?:mm)?)\b/gi.test(String(defaultTitle))) {
        // Always use default title for dimension fields card (not imported headers)
        // This ensures the label always shows the pattern (xxmm, xxxmm) for user input

        // Determine category based on field key prefix
        const category = col.key.startsWith("macadam") ? "macadam" : "paving";

        fieldsWithDimensions.push({
          key: col.key,
          title: String(defaultTitle), // Always use default title
          category,
        });
      }
    });

    return fieldsWithDimensions;
  }, []); // Remove columnHeaders dependency since we always use default titles

  const baseColumns = [
    {
      title: "Plot No",
      dataIndex: "plotNo",
      key: "plotNo",
      width: 110,
      fixed: "left" as const,
      render: (text: string, record: PlotPavingsRow) => (
        <BufferedTextInput
          value={text}
          onCommit={(newValue) => {
            if (isDuplicatePlotNo(data, record.key, newValue)) {
              message.error(`Plot No "${newValue}" is already in use.`);
              return;
            }
            handleInputChange(record.key, "plotNo", newValue);
          }}
          placeholder="Plot No"
        />
      ),
    },
    {
      title: "House type",
      dataIndex: "housetype",
      key: "housetype",
      width: 110,
      fixed: "left" as const,
      render: (text: string, record: PlotPavingsRow) => (
        <Input
          value={text}
          onChange={(e) =>
            handleInputChange(record.key, "housetype", e.target.value)
          }
          placeholder="House type"
        />
      ),
    },
  ];

  // Paving Paths tab columns
  const pavingPathsColumns = React.useMemo(
    () => addColumnCodes([
      ...baseColumns,
      {
        title: formatColumnTitle(`${getColumnTitle("pavingLevelCompact", "Level and compact formation")} (m²)`),
        dataIndex: "pavingLevelCompact",
        key: "pavingLevelCompact",
        width: 110,
        render: (value: number, record: PlotPavingsRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "pavingLevelCompact", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle(`${getColumnTitle("pavingGeotextileLayer", "Geotextile Layer")} (m²)`),
        dataIndex: "pavingGeotextileLayer",
        key: "pavingGeotextileLayer",
        width: 110,
        render: (value: number, record: PlotPavingsRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(
                record.key,
                "pavingGeotextileLayer",
                newValue
              )
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle(`${getColumnTitle("pavingSubBase", "xxxmm thick Stone bed")} (m²)`),
        dataIndex: "pavingSubBase",
        key: "pavingSubBase",
        width: 110,
        render: (value: number, record: PlotPavingsRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "pavingSubBase", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle(`${getColumnTitle("pavingPlainGreyPavings", "Slabs")} (m²)`),
        dataIndex: "pavingPlainGreyPavings",
        key: "pavingPlainGreyPavings",
        width: 110,
        render: (value: number, record: PlotPavingsRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(
                record.key,
                "pavingPlainGreyPavings",
                newValue
              )
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle(`${getColumnTitle("slab900wSteps", "EO for forming steps - 900mm wide")} (Nr)`),
        dataIndex: "slab900wSteps",
        key: "slab900wSteps",
        width: 110,
        render: (value: number, record: PlotPavingsRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "slab900wSteps", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle(`${getColumnTitle("slab1200wSteps", "EO for forming steps - 1200mm wide")} (Nr)`),
        dataIndex: "slab1200wSteps",
        key: "slab1200wSteps",
        width: 110,
        render: (value: number, record: PlotPavingsRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "slab1200wSteps", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle(`${getColumnTitle("slabGravelMarginPea", "10mm/20mm gravel in 150mm wide margin to house path")} (Nr)`),
        dataIndex: "slabGravelMarginPea",
        key: "slabGravelMarginPea",
        width: 150,
        render: (value: number, record: PlotPavingsRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "slabGravelMarginPea", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle(`${getColumnTitle("slabGravelMarginCotswold", "Cotswold gravel in 150mm wide margin to house path")} (Nr)`),
        dataIndex: "slabGravelMarginCotswold",
        key: "slabGravelMarginCotswold",
        width: 170,
        render: (value: number, record: PlotPavingsRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(
                record.key,
                "slabGravelMarginCotswold",
                newValue
              )
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle(`${getColumnTitle("slabThresholdDrain", "Threshold drain to fronts only")} (Nr)`),
        dataIndex: "slabThresholdDrain",
        key: "slabThresholdDrain",
        width: 110,
        render: (value: number, record: PlotPavingsRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "slabThresholdDrain", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle(`${getColumnTitle("slabShedBase", "Shed Base")} (Nr)`),
        dataIndex: "slabShedBase",
        key: "slabShedBase",
        width: 110,
        render: (value: number, record: PlotPavingsRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "slabShedBase", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle(`${getColumnTitle("slabCycleHoops", "Sheffield cycle hoops")} (Nr)`),
        dataIndex: "slabCycleHoops",
        key: "slabCycleHoops",
        width: 110,
        render: (value: number, record: PlotPavingsRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "slabCycleHoops", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle(`${getColumnTitle("slabRotaryDryer", "Fix only rotary dryers")} (Nr)`),
        dataIndex: "slabRotaryDryer",
        key: "slabRotaryDryer",
        width: 130,
        render: (value: number, record: PlotPavingsRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "slabRotaryDryer", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: "Actions",
        key: "actions",
        width: 110,
        fixed: "right" as const,
        align: "center" as const,
        render: (_, record: PlotPavingsRow) => (
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => deleteRow(record.key)}
            size="small"
          />
        ),
      },
    ], effectivePavingsRefs),
    [baseColumns, getColumnTitle, effectivePavingsRefs]
  );

  // Macadam Paths tab columns
  const macadamPathsColumns = React.useMemo(
    () => addColumnCodes([
      ...baseColumns,
      {
        title: formatColumnTitle(`${getColumnTitle("macadamLevelCompact", "Level and compact formation")} (m²)`),
        dataIndex: "macadamLevelCompact",
        key: "macadamLevelCompact",
        width: 110,
        render: (value: number, record: PlotPavingsRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "macadamLevelCompact", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle(`${getColumnTitle("macadamGeotextileLayer", "Geotextile Layer")} (m²)`),
        dataIndex: "macadamGeotextileLayer",
        key: "macadamGeotextileLayer",
        width: 110,
        render: (value: number, record: PlotPavingsRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(
                record.key,
                "macadamGeotextileLayer",
                newValue
              )
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle(`${getColumnTitle("macadamSubBase", "xxxmm thick MOT Type 1")} (m²)`),
        dataIndex: "macadamSubBase",
        key: "macadamSubBase",
        width: 110,
        render: (value: number, record: PlotPavingsRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "macadamSubBase", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle(`${getColumnTitle("macadamDbBase", "xxmm DB  Base")} (m²)`),
        dataIndex: "macadamDbBase",
        key: "macadamDbBase",
        width: 110,
        render: (value: number, record: PlotPavingsRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "macadamDbBase", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle(`${getColumnTitle("macadamDbBinder", "xxmm DB Binder")} (m²)`),
        dataIndex: "macadamDbBinder",
        key: "macadamDbBinder",
        width: 110,
        render: (value: number, record: PlotPavingsRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "macadamDbBinder", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle(`${getColumnTitle("macadamSealingGrit", "Sealing Grit")} (m²)`),
        dataIndex: "macadamSealingGrit",
        key: "macadamSealingGrit",
        width: 110,
        render: (value: number, record: PlotPavingsRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "macadamSealingGrit", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle(`${getColumnTitle("macadamSweepTack", "Sweep & Tack coat")} (m²)`),
        dataIndex: "macadamSweepTack",
        key: "macadamSweepTack",
        width: 110,
        render: (value: number, record: PlotPavingsRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "macadamSweepTack", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle(`${getColumnTitle("macadamDbSurfaceCourse", "xxmm DB Surface course")} (m²)`),
        dataIndex: "macadamDbSurfaceCourse",
        key: "macadamDbSurfaceCourse",
        width: 140,
        render: (value: number, record: PlotPavingsRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(
                record.key,
                "macadamDbSurfaceCourse",
                newValue
              )
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle(`${getColumnTitle("macadamHraSurfaceCourse", "xxmm HRA Surface course")} (m²)`),
        dataIndex: "macadamHraSurfaceCourse",
        key: "macadamHraSurfaceCourse",
        width: 150,
        render: (value: number, record: PlotPavingsRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(
                record.key,
                "macadamHraSurfaceCourse",
                newValue
              )
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle(`${getColumnTitle("macadamSmaSurfaceCourse", "xxmm SMA Surface course")} (m²)`),
        dataIndex: "macadamSmaSurfaceCourse",
        key: "macadamSmaSurfaceCourse",
        width: 150,
        render: (value: number, record: PlotPavingsRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(
                record.key,
                "macadamSmaSurfaceCourse",
                newValue
              )
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle(`${getColumnTitle("macadamEdgings", "Edgings 150 x 50")} (m)`),
        dataIndex: "macadamEdgings",
        key: "macadamEdgings",
        width: 110,
        render: (value: number, record: PlotPavingsRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "macadamEdgings", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: "Actions",
        key: "actions",
        width: 110,
        fixed: "right" as const,
        align: "center" as const,
        render: (_, record: PlotPavingsRow) => (
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => deleteRow(record.key)}
            size="small"
          />
        ),
      },
    ], effectivePavingsRefs),
    [baseColumns, getColumnTitle, effectivePavingsRefs]
  );

  return (
    <MainLayout showFooter={false}>
      <Card
        className="shadow-sm plot-pavings-card"
        bodyStyle={{ padding: 0 }}
        style={{ borderRadius: "8px", overflow: "hidden" }}
      >
        {/* Sticky Header with Title and Tabs */}
        <div
          className="plot-pavings-sticky-header"
          style={{
            position: "sticky",
            top: "144px",
            backgroundColor: "#fff",
            zIndex: 98,
            borderRadius: "8px 8px 0 0",
            isolation: "isolate",
          }}
        >
          {/* Header Section */}
          <div
            className="px-6 pt-6 pb-4"
            style={{ borderBottom: "1px solid #e5e7eb" }}
          >
            <div className="flex justify-between items-center">
              <div>
                <Title
                  level={3}
                  style={{
                    fontWeight: 600,
                    fontSize: "20px",
                    color: "#111827",
                    marginBottom: "8px",
                  }}
                >
                  Plot Paving
                </Title>
                <Text
                  type="secondary"
                  style={{ fontSize: "14px", color: "#6B7280" }}
                >
                  Configure paving and macadam path specifications for each
                  plot.
                </Text>
              </div>
              <Button
                icon={<SettingOutlined />}
                onClick={() => setConfigModalVisible(true)}
                type="default"
                className="configure-headers-btn"
                style={{
                  height: "32px",
                  borderRadius: "6px",
                  fontWeight: 500,
                  borderColor: "#1890ff",
                  color: "#1890ff",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  flexShrink: 0,
                  marginTop: "18px",
                }}
              >
                Configure Headers
              </Button>
            </div>
          </div>

          {/* Tab Buttons */}
          <div
            className="plot-pavings-sticky-tabs px-6"
            style={{ paddingTop: "24px", paddingBottom: "0px" }}
          >
            <div
              style={{
                marginBottom: "0px",
                display: "flex",
                gap: "4px",
                flexWrap: "wrap",
              }}
            >
              <Button
                type={activeTab === "paving-paths" ? "primary" : "default"}
                onClick={() => setActiveTab("paving-paths")}
                className={
                  activeTab === "paving-paths"
                    ? "active-tab-button"
                    : "inactive-tab-button"
                }
                style={{
                  backgroundColor:
                    activeTab === "paving-paths" ? COLORS.primary : "#E1E6EF",
                  borderColor:
                    activeTab === "paving-paths" ? COLORS.primary : "#E1E6EF",
                  color: activeTab === "paving-paths" ? "#fff" : "#6B7280",
                  fontWeight: 500,
                  borderRadius: "6px 6px 0 0",
                  height: "38px",
                  paddingLeft: "20px",
                  paddingRight: "20px",
                  borderBottom:
                    activeTab === "paving-paths" ? "none" : "1px solid #E1E6EF",
                  position: "relative",
                  zIndex: activeTab === "paving-paths" ? 2 : 1,
                }}
              >
                Paving Paths
              </Button>
              <Button
                type={activeTab === "macadam-paths" ? "primary" : "default"}
                onClick={() => setActiveTab("macadam-paths")}
                className={
                  activeTab === "macadam-paths"
                    ? "active-tab-button"
                    : "inactive-tab-button"
                }
                style={{
                  backgroundColor:
                    activeTab === "macadam-paths" ? COLORS.primary : "#E1E6EF",
                  borderColor:
                    activeTab === "macadam-paths" ? COLORS.primary : "#E1E6EF",
                  color: activeTab === "macadam-paths" ? "#fff" : "#6B7280",
                  fontWeight: 500,
                  borderRadius: "6px 6px 0 0",
                  height: "38px",
                  paddingLeft: "20px",
                  paddingRight: "20px",
                  borderBottom:
                    activeTab === "macadam-paths"
                      ? "none"
                      : "1px solid #E1E6EF",
                  position: "relative",
                  zIndex: activeTab === "macadam-paths" ? 2 : 1,
                }}
              >
                Macadam Paths
              </Button>
              {selectedPavingKeys.length > 0 && (
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  onClick={deleteSelectedRows}
                  style={{
                    height: "32px",
                    marginLeft: "auto",
                    borderRadius: "6px",
                    fontWeight: 500,
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  Delete Selected ({selectedPavingKeys.length})
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Content Section - Tables */}
        <div
          className="px-6 pb-6 plot-pavings-content-section"
          style={{
            paddingTop: "0px",
            position: "relative",
            zIndex: 1,
            marginTop: "0px",
          }}
        >
          {/* Tab Content - Paving Paths */}
          {activeTab === "paving-paths" && (
            <div>
              <div
                className="plot-pavings-sticky-dimension-paving"
                style={{
                  position: "sticky",
                  top:
                    144 +
                    document
                      .getElementsByClassName("plot-pavings-sticky-header")?.[0]
                      ?.getBoundingClientRect?.()?.height +
                    "px",
                  backgroundColor: "#fff",
                  zIndex: 98,
                }}
              />

              {isTableLoading ? (
                <TableLoadingPlaceholder />
              ) : (
              <Table
                key={tableKey}
                dataSource={data}
                columns={pavingPathsColumns}
                pagination={false}
                rowSelection={{
                  selectedRowKeys: selectedPavingKeys,
                  onChange: setSelectedPavingKeys,
                  fixed: "left",
                  columnWidth: 48,
                }}
                scroll={getScrollConfig(pavingPathsColumns)}
                sticky={{
                  offsetHeader:
                    144 +
                    document
                      .getElementsByClassName("plot-pavings-sticky-header")?.[0]
                      ?.getBoundingClientRect?.()?.height +
                    document
                      .getElementsByClassName(
                        "plot-pavings-sticky-dimension-paving"
                      )?.[0]
                      ?.getBoundingClientRect?.()?.height,
                }}
                size="middle"
                bordered
                className="plot-pavings-table"
                summary={() => {
                  if (data.length === 0) return null;
                  const totals = calculateTotals();
                  return (
                    <Table.Summary fixed="bottom">
                      <Table.Summary.Row
                        style={{ backgroundColor: "#FFF7E6", fontWeight: 600 }}
                      >
                        <Table.Summary.Cell index={0} colSpan={3} align="center">
                          <span style={{ color: "#000" }}>Totals</span>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell align="right" index={3}><span style={{ color: "#000", textAlign: "right", display: "block", paddingRight: "8px" }}>{(totals.pavingLevelCompact ?? 0).toFixed(3)}</span></Table.Summary.Cell>
                        <Table.Summary.Cell align="right" index={4}><span style={{ color: "#000", textAlign: "right", display: "block", paddingRight: "8px" }}>{(totals.pavingGeotextileLayer ?? 0).toFixed(3)}</span></Table.Summary.Cell>
                        <Table.Summary.Cell align="right" index={5}><span style={{ color: "#000", textAlign: "right", display: "block", paddingRight: "8px" }}>{(totals.pavingSubBase ?? 0).toFixed(3)}</span></Table.Summary.Cell>
                        <Table.Summary.Cell align="right" index={6}><span style={{ color: "#000", textAlign: "right", display: "block", paddingRight: "8px" }}>{(totals.pavingPlainGreyPavings ?? 0).toFixed(3)}</span></Table.Summary.Cell>
                        <Table.Summary.Cell align="right" index={7}><span style={{ color: "#000", textAlign: "right", display: "block", paddingRight: "8px" }}>{(totals.slab900wSteps ?? 0).toFixed(3)}</span></Table.Summary.Cell>
                        <Table.Summary.Cell align="right" index={8}><span style={{ color: "#000", textAlign: "right", display: "block", paddingRight: "8px" }}>{(totals.slab1200wSteps ?? 0).toFixed(3)}</span></Table.Summary.Cell>
                        <Table.Summary.Cell align="right" index={9}><span style={{ color: "#000", textAlign: "right", display: "block", paddingRight: "8px" }}>{(totals.slabGravelMarginPea ?? 0).toFixed(3)}</span></Table.Summary.Cell>
                        <Table.Summary.Cell align="right" index={10}><span style={{ color: "#000", textAlign: "right", display: "block", paddingRight: "8px" }}>{(totals.slabGravelMarginCotswold ?? 0).toFixed(3)}</span></Table.Summary.Cell>
                        <Table.Summary.Cell align="right" index={11}><span style={{ color: "#000", textAlign: "right", display: "block", paddingRight: "8px" }}>{(totals.slabThresholdDrain ?? 0).toFixed(3)}</span></Table.Summary.Cell>
                        <Table.Summary.Cell align="right" index={12}><span style={{ color: "#000", textAlign: "right", display: "block", paddingRight: "8px" }}>{(totals.slabShedBase ?? 0).toFixed(3)}</span></Table.Summary.Cell>
                        <Table.Summary.Cell align="right" index={13}><span style={{ color: "#000", textAlign: "right", display: "block", paddingRight: "8px" }}>{(totals.slabCycleHoops ?? 0).toFixed(3)}</span></Table.Summary.Cell>
                        <Table.Summary.Cell align="right" index={14}><span style={{ color: "#000", textAlign: "right", display: "block", paddingRight: "8px" }}>{(totals.slabRotaryDryer ?? 0).toFixed(3)}</span></Table.Summary.Cell>
                        <Table.Summary.Cell index={15}></Table.Summary.Cell>
                      </Table.Summary.Row>
                    </Table.Summary>
                  );
                }}
              />
              )}
              <div style={{ marginTop: "16px" }}>
                <Button
                  type="text"
                  icon={<PlusOutlined style={{ color: COLORS.primary }} />}
                  onClick={addRow}
                  style={{
                    color: COLORS.primary,
                    fontWeight: 500,
                    padding: "4px 8px",
                  }}
                >
                  Add New Row
                </Button>
              </div>
            </div>
          )}

          {/* Tab Content - Macadam Paths */}
          {activeTab === "macadam-paths" && (
            <div>
              <div
                className="plot-pavings-sticky-dimension-macadam"
                style={{
                  position: "sticky",
                  top:
                    144 +
                    document
                      .getElementsByClassName("plot-pavings-sticky-header")?.[0]
                      ?.getBoundingClientRect?.()?.height +
                    "px",
                  backgroundColor: "#fff",
                  zIndex: 98,
                }}
              />

              {isTableLoading ? (
                <TableLoadingPlaceholder />
              ) : (
              <Table
                key={tableKey}
                dataSource={data}
                columns={macadamPathsColumns}
                pagination={false}
                rowSelection={{
                  selectedRowKeys: selectedPavingKeys,
                  onChange: setSelectedPavingKeys,
                  fixed: "left",
                  columnWidth: 48,
                }}
                scroll={getScrollConfig(macadamPathsColumns)}
                sticky={{
                  offsetHeader:
                    144 +
                    document
                      .getElementsByClassName("plot-pavings-sticky-header")?.[0]
                      ?.getBoundingClientRect?.()?.height +
                    document
                      .getElementsByClassName(
                        "plot-pavings-sticky-dimension-macadam"
                      )?.[0]
                      ?.getBoundingClientRect?.()?.height,
                }}
                size="middle"
                bordered
                className="plot-pavings-table"
                summary={() => {
                  if (data.length === 0) return null;
                  const totals = calculateTotals();
                  return (
                    <Table.Summary fixed="bottom">
                      <Table.Summary.Row
                        style={{ backgroundColor: "#FFF7E6", fontWeight: 600 }}
                      >
                        <Table.Summary.Cell index={0} colSpan={3} align="center">
                          <span style={{ color: "#000" }}>Totals</span>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell align="right" index={3}><span style={{ color: "#000", textAlign: "right", display: "block", paddingRight: "8px" }}>{(totals.macadamLevelCompact ?? 0).toFixed(3)}</span></Table.Summary.Cell>
                        <Table.Summary.Cell align="right" index={4}><span style={{ color: "#000", textAlign: "right", display: "block", paddingRight: "8px" }}>{(totals.macadamGeotextileLayer ?? 0).toFixed(3)}</span></Table.Summary.Cell>
                        <Table.Summary.Cell align="right" index={5}><span style={{ color: "#000", textAlign: "right", display: "block", paddingRight: "8px" }}>{(totals.macadamSubBase ?? 0).toFixed(3)}</span></Table.Summary.Cell>
                        <Table.Summary.Cell align="right" index={6}><span style={{ color: "#000", textAlign: "right", display: "block", paddingRight: "8px" }}>{(totals.macadamDbBase ?? 0).toFixed(3)}</span></Table.Summary.Cell>
                        <Table.Summary.Cell align="right" index={7}><span style={{ color: "#000", textAlign: "right", display: "block", paddingRight: "8px" }}>{(totals.macadamDbBinder ?? 0).toFixed(3)}</span></Table.Summary.Cell>
                        <Table.Summary.Cell align="right" index={8}><span style={{ color: "#000", textAlign: "right", display: "block", paddingRight: "8px" }}>{(totals.macadamSealingGrit ?? 0).toFixed(3)}</span></Table.Summary.Cell>
                        <Table.Summary.Cell align="right" index={9}><span style={{ color: "#000", textAlign: "right", display: "block", paddingRight: "8px" }}>{(totals.macadamSweepTack ?? 0).toFixed(3)}</span></Table.Summary.Cell>
                        <Table.Summary.Cell align="right" index={10}><span style={{ color: "#000", textAlign: "right", display: "block", paddingRight: "8px" }}>{(totals.macadamDbSurfaceCourse ?? 0).toFixed(3)}</span></Table.Summary.Cell>
                        <Table.Summary.Cell align="right" index={11}><span style={{ color: "#000", textAlign: "right", display: "block", paddingRight: "8px" }}>{(totals.macadamHraSurfaceCourse ?? 0).toFixed(3)}</span></Table.Summary.Cell>
                        <Table.Summary.Cell align="right" index={12}><span style={{ color: "#000", textAlign: "right", display: "block", paddingRight: "8px" }}>{(totals.macadamSmaSurfaceCourse ?? 0).toFixed(3)}</span></Table.Summary.Cell>
                        <Table.Summary.Cell align="right" index={13}><span style={{ color: "#000", textAlign: "right", display: "block", paddingRight: "8px" }}>{(totals.macadamEdgings ?? 0).toFixed(3)}</span></Table.Summary.Cell>
                        <Table.Summary.Cell index={14}></Table.Summary.Cell>
                      </Table.Summary.Row>
                    </Table.Summary>
                  );
                }}
              />
              )}
              <div style={{ marginTop: "16px" }}>
                <Button
                  type="text"
                  icon={<PlusOutlined style={{ color: COLORS.primary }} />}
                  onClick={addRow}
                  style={{
                    color: COLORS.primary,
                    fontWeight: 500,
                    padding: "4px 8px",
                  }}
                >
                  Add New Row
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Footer Section */}
        <div style={{ borderTop: "1px solid #e5e7eb" }}>
          <div className="px-6 py-4 flex justify-end gap-3">
            <Button
              onClick={handleSaveAndExit}
              style={{
                borderColor: COLORS.primary,
                color: COLORS.primary,
              }}
            >
              Save and Exit
            </Button>
            <Button
              type="primary"
              onClick={handleSaveAndContinue}
              style={{
                backgroundColor: COLORS.primary,
                borderColor: COLORS.primary,
              }}
            >
              Save & Continue <ArrowRightOutlined />
            </Button>
          </div>
        </div>
      </Card>

      {/* Hidden Excel Import/Export Component */}
      <div style={{ display: "none" }}>
        <ExcelImportExport
          onExport={handleExportExcel}
          onImport={handleImportExcel}
          onDownloadTemplate={handleDownloadTemplate}
          importLoading={importLoading}
          importModalVisible={importModalVisible}
          setImportModalVisible={setImportModalVisible}
          hasData={data.length > 0}
        />
      </div>

      <style>{`
        .plot-pavings-card.ant-card {
          border-radius: 8px !important;
          overflow: visible !important;
        }

        .plot-pavings-card.ant-card .ant-card-body {
          border-radius: 8px !important;
          overflow: visible !important;
          position: relative !important;
        }

        .plot-pavings-sticky-header {
          box-shadow: none;
          will-change: transform;
          transform: translateZ(0);
          z-index: 98 !important;
        }

        .plot-pavings-sticky-tabs {
          border-bottom: none;
          min-height: 40px;
        }

        .plot-pavings-card {
          position: relative;
          height: auto;
          overflow: visible !important;
        }

        .plot-pavings-card .ant-card-body {
          overflow: visible !important;
        }

        .plot-pavings-content-section {
          clip-path: inset(0px -100vw -100vh -100vw);
        }

        .plot-pavings-table .ant-table-body {
          margin-top: 0 !important;
        }

        .active-tab-button {
          border-bottom: none !important;
        }

        .inactive-tab-button {
          border-bottom: none !important;
        }

        .plot-pavings-table {
          font-size: 14px;
          margin-top: 0 !important;
        }

        .plot-pavings-table .ant-table-wrapper {
          margin-top: -1px !important;
        }

        .plot-pavings-table .ant-table-container {
          margin-top: 0 !important;
        }

        .plot-pavings-table .ant-table {
          border: none !important;
          margin-top: 0 !important;
        }

        .plot-pavings-table .ant-table-container {
          border: 1px solid #e5e7eb !important;
          border-top: none !important;
          border-radius: 0 !important;
        }

        .plot-pavings-table .ant-table-thead > tr > th {
          min-width: 110px !important;
          background-color: ${COLORS.tableHeaderBg} !important;
          font-weight: 600 !important;
          color: #000 !important;
          border-right: 1px solid #e5e7eb !important;
          border-bottom: 1px solid #e5e7eb !important;
          border-left: none !important;
          border-top: none !important;
          padding: 24px 8px 10px 8px !important;
          text-align: center !important;
          font-size: 13px !important;
          will-change: transform !important;
          transform: translateZ(0) !important;
          z-index: 97 !important;
        }

        .plot-pavings-table .ant-table-thead > tr > th:first-child {
          border-left: none !important;
        }
.plot-pavings-table .ant-table-thead > tr > th.ant-table-cell-fix-left {
          z-index: 99 !important;
        }

        .plot-pavings-table .ant-table-thead > tr > th.ant-table-cell-fix-right {
          z-index: 100 !important;
          background-color: ${COLORS.tableHeaderBg} !important;
        }

        .plot-pavings-table .ant-table-tbody > tr > td.ant-table-cell-fix-right {
          z-index: 2 !important;
          background-color: #fff !important;
        }

        .plot-pavings-table .ant-table-tbody > tr:hover > td.ant-table-cell-fix-right {
          background-color: #fafafa !important;
        }
        /* The generic min-width:110px above also matches the rowSelection
           checkbox column since it's a plain th/td. Keep it pinned to the
           rowSelection columnWidth instead of being stretched. */
        .plot-pavings-table .ant-table-thead > tr > th.ant-table-selection-column,
        .plot-pavings-table .ant-table-tbody > tr > td.ant-table-selection-column {
          min-width: 48px !important;
          width: 48px !important;
        }

        .plot-pavings-table .ant-table-tbody > tr > td {
          min-width: 110px !important;
          padding: 6px 8px !important;
          border-right: 1px solid #e5e7eb !important;
          border-bottom: 1px solid #e5e7eb !important;
          border-left: none !important;
          border-top: none !important;
          background-color: #fff;
        }

        .plot-pavings-table .ant-table-tbody > tr > td:first-child {
          border-left: none !important;
        }

        .plot-pavings-table .ant-table-tbody > tr:hover > td {
          background-color: #fafafa !important;
        }

        .plot-pavings-table .ant-table-tbody > tr.ant-table-row-selected > td {
          background-color: #fff !important;
        }

        .plot-pavings-table .ant-input {
          border: 1px solid #d9d9d9 !important;
          border-radius: 4px !important;
          padding: 4px 11px !important;
          font-size: 14px !important;
          height: 32px !important;
        }

        /* Center-align text inputs by default (for Plot No, Housetype) */
        .plot-pavings-table .ant-input[type="text"] {
          text-align: center !important;
        }

        /* Right-align NumberInput components (exclude first 2 columns: Plot No, Housetype) */
        .plot-pavings-table td:not(:nth-child(1)):not(:nth-child(2)) .ant-input[type="text"] {
          text-align: right !important;
        }

        .plot-pavings-table .ant-input[type="number"] {
          text-align: right !important;
        }

        .plot-pavings-table .ant-input:focus {
          border-color: ${COLORS.primary} !important;
          box-shadow: 0 0 0 2px rgba(22, 126, 249, 0.1) !important;
        }

        .plot-pavings-table .ant-input-number {
          width: 100%;
        }

        .plot-pavings-table .ant-table-summary > tr > td {
          background-color: #FFF7E6 !important;
          border-right: 1px solid #e5e7eb !important;
          border-bottom: none !important;
          border-left: none !important;
          border-top: none !important;
          padding: 10px 8px !important;
          font-weight: 600 !important;
        }

        .plot-pavings-table .ant-table-summary > tr > td:first-child {
          border-left: none !important;
        }

        .plot-pavings-table .ant-btn-text.ant-btn-dangerous {
          color: #ff4d4f !important;
        }

        .plot-pavings-table .ant-btn-text.ant-btn-dangerous:hover {
          background-color: #fff1f0 !important;
        }
      `}</style>

      {/* Column Configuration Modal */}
      <ColumnConfigModal
        title="Configure Headers"
        open={configModalVisible}
        onClose={() => {
          // Reset temp values back to actual values when modal closes without applying
          setTempDimensionValues({ ...dimensionValues });
          setTempColumnHeaders({ ...columnHeaders });
          setConfigModalVisible(false);
        }}
        onApply={() => {
          // Apply temporary changes to actual state
          setDimensionValues(tempDimensionValues);
          setColumnHeaders(tempColumnHeaders);
        }}
        width={500}
        isViewMode={tender.isViewMode}
      >
        {activeTab === "paving-paths" ? (
          <div className="grid grid-cols-2 gap-3">
            {/* Sub base dynamic input */}
            <div>
              <Text className="text-xs mb-1 block">xxxmm thick Stone bed (m²)</Text>
              <NumberInput
                value={(() => {
                  const raw = parseFloat(tempDimensionValues["pavingSubBase"]) || 0;
                  return raw >= 1 ? raw / 1000 : raw;
                })()}
                onChange={(value) => {
                  const metresValue = value ? parseFloat(value.toString()) : 0;
                  if (!metresValue) {
                    setTempDimensionValues((prev) => {
                      const newValues = { ...prev };
                      delete newValues["pavingSubBase"];
                      return newValues;
                    });
                    setTempColumnHeaders((prev) => {
                      const newHeaders = { ...prev };
                      delete newHeaders["pavingSubBase"];
                      return newHeaders;
                    });
                  } else {
                    const mmValue = String(Math.round(metresValue * 1000));
                    setTempDimensionValues((prev) => ({
                      ...prev,
                      pavingSubBase: mmValue,
                    }));
                    setTempColumnHeaders((prev) => ({
                      ...prev,
                      pavingSubBase: `${mmValue}mm thick Stone bed`,
                    }));
                  }
                }}
                positiveOnly
                decimalPlaces={3}
                zeroIntegerOnly
              />
            </div>
            {/* Slabs dropdown */}
            <div>
              <Text className="text-xs mb-1 block">Slabs (m²)</Text>
              <select
                value={tempDimensionValues["pavingPlainGreyPavings"] || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  if (!value) {
                    setTempDimensionValues((prev) => {
                      const newValues = { ...prev };
                      delete newValues["pavingPlainGreyPavings"];
                      return newValues;
                    });
                    setTempColumnHeaders((prev) => {
                      const newHeaders = { ...prev };
                      delete newHeaders["pavingPlainGreyPavings"];
                      return newHeaders;
                    });
                  } else {
                    setTempDimensionValues((prev) => ({
                      ...prev,
                      pavingPlainGreyPavings: value,
                    }));
                    const selectedCode = value as EvaluateCode;
                    const label = EVALUATE_CODES[selectedCode]?.label || value;
                    setTempColumnHeaders((prev) => ({
                      ...prev,
                      pavingPlainGreyPavings: label,
                    }));
                  }
                }}
                style={selectStyles}
              >
                <option value="">-- Select --</option>
                <option value="GWALP007">Plain Grey slabs on 50mm sand beds</option>
                <option value="GWALP008">Buff / Grey riven slabs on 50mm sand beds</option>
                <option value="GWALP040">Plain Grey Slabs 450x450 on 50mm beds</option>
                <option value="GWALP041">Torver Buff Textured slabs 600x600 on 50mm beds</option>
                <option value="GWALP042">Marshalls Textured Utility slabs 450x450 on 50mm beds</option>
                <option value="GWALP043">Marshalls Pendle Buff Riven slabs 600x600 on 50mm beds</option>
                <option value="GWALP044">Marshalls Saxon Buff Riven slabs 600x600 on 50mm beds</option>
                <option value="GWALP045">Bradstone Textured Buff slabs 450x450 on 50mm beds</option>
              </select>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {/* Macadam Sub base - thick MOT Type 1 */}
            <div>
              <Text className="text-xs mb-1 block">xxxmm thick MOT Type 1 (m²)</Text>
              <NumberInput
                value={(() => {
                  const raw = parseFloat(tempDimensionValues["macadamSubBase"]) || 0;
                  return raw >= 1 ? raw / 1000 : raw;
                })()}
                onChange={(value) => {
                  const metresValue = value ? parseFloat(value.toString()) : 0;
                  if (!metresValue) {
                    setTempDimensionValues((prev) => {
                      const newValues = { ...prev };
                      delete newValues["macadamSubBase"];
                      return newValues;
                    });
                    setTempColumnHeaders((prev) => {
                      const newHeaders = { ...prev };
                      delete newHeaders["macadamSubBase"];
                      return newHeaders;
                    });
                  } else {
                    const mmValue = String(Math.round(metresValue * 1000));
                    setTempDimensionValues((prev) => ({
                      ...prev,
                      macadamSubBase: mmValue,
                    }));
                    setTempColumnHeaders((prev) => ({
                      ...prev,
                      macadamSubBase: `${mmValue}mm thick MOT Type 1`,
                    }));
                  }
                }}
                positiveOnly
                decimalPlaces={3}
                zeroIntegerOnly
              />
            </div>
            {/* DB Base */}
            <div>
              <Text className="text-xs mb-1 block">xxmm DB Base (m²)</Text>
              <select
                value={tempDimensionValues["macadamDbBase"] || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  if (!value) {
                    setTempDimensionValues((prev) => {
                      const newValues = { ...prev };
                      delete newValues["macadamDbBase"];
                      return newValues;
                    });
                    setTempColumnHeaders((prev) => {
                      const newHeaders = { ...prev };
                      delete newHeaders["macadamDbBase"];
                      return newHeaders;
                    });
                  } else {
                    setTempDimensionValues((prev) => ({
                      ...prev,
                      macadamDbBase: value,
                    }));
                    const selectedCode = value as EvaluateCode;
                    const label = EVALUATE_CODES[selectedCode]?.label || value;
                    setTempColumnHeaders((prev) => ({
                      ...prev,
                      macadamDbBase: label,
                    }));
                  }
                }}
                style={selectStyles}
              >
                <option value="">-- Select --</option>
                <option value="ALP041">100mm thick AC 32mm dense bin</option>
                <option value="ALP042">150mm thick AC 32mm dense bin</option>
              </select>
            </div>
            {/* DB Binder */}
            <div>
              <Text className="text-xs mb-1 block">xxmm DB Binder (m²)</Text>
              <select
                value={tempDimensionValues["macadamDbBinder"] || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  if (!value) {
                    setTempDimensionValues((prev) => {
                      const newValues = { ...prev };
                      delete newValues["macadamDbBinder"];
                      return newValues;
                    });
                    setTempColumnHeaders((prev) => {
                      const newHeaders = { ...prev };
                      delete newHeaders["macadamDbBinder"];
                      return newHeaders;
                    });
                  } else {
                    setTempDimensionValues((prev) => ({
                      ...prev,
                      macadamDbBinder: value,
                    }));
                    const selectedCode = value as EvaluateCode;
                    const label = EVALUATE_CODES[selectedCode]?.label || value;
                    setTempColumnHeaders((prev) => ({
                      ...prev,
                      macadamDbBinder: label,
                    }));
                  }
                }}
                style={selectStyles}
              >
                <option value="">-- Select --</option>
                <option value="ALP043">50mm thick AC 20mm dense bin</option>
                <option value="ALP044">60mm thick AC 20mm dense bin</option>
                <option value="ALP045">75mm thick AC 20mm dense bin</option>
                <option value="ALP046">80mm thick AC 20mm dense bin</option>
                <option value="ALP047">100mm thick AC 20mm dense bin</option>
              </select>
            </div>
            {/* DB Surface Course */}
            <div>
              <Text className="text-xs mb-1 block">
                xxmm DB Surface course (m²)
              </Text>
              <select
                value={tempDimensionValues["macadamDbSurfaceCourse"] || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  if (!value) {
                    setTempDimensionValues((prev) => {
                      const newValues = { ...prev };
                      delete newValues["macadamDbSurfaceCourse"];
                      return newValues;
                    });
                    setTempColumnHeaders((prev) => {
                      const newHeaders = { ...prev };
                      delete newHeaders["macadamDbSurfaceCourse"];
                      return newHeaders;
                    });
                  } else {
                    setTempDimensionValues((prev) => ({
                      ...prev,
                      macadamDbSurfaceCourse: value,
                    }));
                    const selectedCode = value as EvaluateCode;
                    const label = EVALUATE_CODES[selectedCode]?.label || value;
                    setTempColumnHeaders((prev) => ({
                      ...prev,
                      macadamDbSurfaceCourse: label,
                    }));
                  }
                }}
                style={selectStyles}
              >
                <option value="">-- Select --</option>
                <option value="ALP048">20mm thick AC 6mm dense surf</option>
                <option value="ALP049">25mm thick AC 6 or 10mm dense surf</option>
                <option value="ALP051">30mm thick AC 6 or 10mm dense surf</option>
              </select>
            </div>
            {/* HRA Surface Course */}
            <div>
              <Text className="text-xs mb-1 block">
                xxmm HRA Surface course (m²)
              </Text>
              <select
                value={tempDimensionValues["macadamHraSurfaceCourse"] || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  if (!value) {
                    setTempDimensionValues((prev) => {
                      const newValues = { ...prev };
                      delete newValues["macadamHraSurfaceCourse"];
                      return newValues;
                    });
                    setTempColumnHeaders((prev) => {
                      const newHeaders = { ...prev };
                      delete newHeaders["macadamHraSurfaceCourse"];
                      return newHeaders;
                    });
                  } else {
                    setTempDimensionValues((prev) => ({
                      ...prev,
                      macadamHraSurfaceCourse: value,
                    }));
                    const selectedCode = value as EvaluateCode;
                    const label = EVALUATE_CODES[selectedCode]?.label || value;
                    setTempColumnHeaders((prev) => ({
                      ...prev,
                      macadamHraSurfaceCourse: label,
                    }));
                  }
                }}
                style={selectStyles}
              >
                <option value="">-- Select --</option>
                <option value="ALP049">25mm thick AC 6 or 10mm dense surf</option>
                <option value="ALP051">30mm thick AC 6 or 10mm dense surf</option>
              </select>
            </div>
            {/* SMA Surface Course */}
            <div>
              <Text className="text-xs mb-1 block">
                xxmm SMA Surface course (m²)
              </Text>
              <select
                value={tempDimensionValues["macadamSmaSurfaceCourse"] || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  if (!value) {
                    setTempDimensionValues((prev) => {
                      const newValues = { ...prev };
                      delete newValues["macadamSmaSurfaceCourse"];
                      return newValues;
                    });
                    setTempColumnHeaders((prev) => {
                      const newHeaders = { ...prev };
                      delete newHeaders["macadamSmaSurfaceCourse"];
                      return newHeaders;
                    });
                  } else {
                    setTempDimensionValues((prev) => ({
                      ...prev,
                      macadamSmaSurfaceCourse: value,
                    }));
                    const selectedCode = value as EvaluateCode;
                    const label = EVALUATE_CODES[selectedCode]?.label || value;
                    setTempColumnHeaders((prev) => ({
                      ...prev,
                      macadamSmaSurfaceCourse: label,
                    }));
                  }
                }}
                style={selectStyles}
              >
                <option value="">-- Select --</option>
                <option value="ALP052">25 or 30mm thick 6 or 10mm SMA</option>
              </select>
            </div>
          </div>
        )}
      </ColumnConfigModal>

      <DeleteConfirmationModal
        visible={deleteModalVisible}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </MainLayout>
  );
};

export default PlotPavings;
